import React, { useState, useEffect } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import { LayoutDashboard, Users, LogOut, Menu, X, Moon, Sun, Loader2, Database, BarChart3 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import NotificationBell from "./NotificationBell";
import SessionManager from "../common/SessionManager";

export default function ClientLayout() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const { isDarkMode, toggleTheme } = useTheme();

  // YouTube-style top loader effect on route change
  useEffect(() => {
    setIsNavigating(true);
    const timeout = setTimeout(() => setIsNavigating(false), 500); 
    return () => clearTimeout(timeout);
  }, [location.pathname]);

  const handleLogout = async () => {
    setIsLoggingOut(true);
    // Artificially delay for a premium feel
    await new Promise(resolve => setTimeout(resolve, 800));
    logout();
    navigate("/login");
  };

  const navItems = [
    { name: "My Leads", href: "/leads", icon: Users },
    { name: "My Access", href: "/access", icon: Database },
    { name: "Reports", href: "/reports", icon: BarChart3 }
  ];

  return (
    <div className="flex h-screen bg-bg-app font-sans text-text-primary relative overflow-hidden">
      <SessionManager />
      {/* Logout Overlay */}
      <AnimatePresence>
        {isLoggingOut && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-bg-app/60 backdrop-blur-md flex flex-col items-center justify-center"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-bg-card p-8 rounded-card shadow-floating border border-border-light flex flex-col items-center gap-4"
            >
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
              <p className="text-[16px] font-bold text-text-primary tracking-tight">Signing you out...</p>
              <p className="text-[13px] text-text-secondary">Securing your session.</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Top Loader */}
      <AnimatePresence>
        {isNavigating && !isLoggingOut && (
          <motion.div
            initial={{ width: "0%", opacity: 1 }}
            animate={{ width: "100%", opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5, ease: "easeInOut" }}
            className="fixed top-0 left-0 h-[3px] bg-primary z-[9999]"
          />
        )}
      </AnimatePresence>

      {/* Sidebar (Desktop) */}
      <aside className="hidden md:flex w-64 flex-col bg-bg-sidebar border-r border-border-light">
        <div className="h-[64px] flex items-center px-6 border-b border-border-light">
          <img src="/logo4.png" alt="JHY Techs" className="h-8 w-auto object-contain mr-3" />
        </div>
        
        <nav className="flex-1 px-4 py-6 space-y-1.5 overflow-y-auto">
          <div className="px-2 pb-2">
            <p className="text-[11px] font-bold uppercase tracking-wider text-text-muted">Main Menu</p>
          </div>
          {navItems.map((item) => {
            const isActive = location.pathname === item.href || (item.href !== "/" && location.pathname.startsWith(item.href));
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`flex items-center px-3 py-2.5 rounded-[8px] transition-all font-medium text-[14px] ${
                  isActive 
                    ? "bg-primary-light text-primary" 
                    : "text-text-secondary hover:bg-bg-soft hover:text-text-primary"
                }`}
              >
                <item.icon className={`w-5 h-5 mr-3 ${isActive ? 'text-primary' : 'text-text-secondary'}`} />
                {item.name}
              </Link>
            );
          })}
        </nav>
        
        <div className="p-4 border-t border-border-light bg-bg-soft/30">
          <div className="flex items-center px-3 py-2 mb-2">
             <div className="w-9 h-9 rounded-[8px] bg-primary-light flex items-center justify-center mr-3 text-[14px] font-bold text-primary shadow-small border border-primary-light">
                {user?.fullName?.charAt(0) || "U"}
             </div>
             <div className="flex-1 min-w-0">
               <p className="text-[14px] font-bold text-text-primary truncate">{user?.fullName || "User"}</p>
               <p className="text-[12px] font-medium text-text-muted truncate">{user?.role || "Client"}</p>
             </div>
          </div>
          <motion.button
            whileTap={{ scale: 0.97 }}
            onClick={handleLogout}
            className="w-full flex items-center justify-center px-4 py-2 text-[14px] font-medium text-text-secondary hover:text-[#E53E3E] hover:bg-[#FFF5F5] rounded-[8px] transition-all focus:outline-none border border-transparent"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </motion.button>
          
          <div className="mt-4 pt-4 border-t border-border-light/30 text-center">
            <p className="text-[10px] font-bold text-text uppercase tracking-[0.2em] opacity-50">
              v0.1.0 • JHY Techs
            </p>
          </div>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className={`flex-1 flex flex-col overflow-hidden transition-all duration-500 ${isLoggingOut ? 'blur-sm scale-[0.99]' : ''}`}>
        {/* Mobile Header */}
        <header className="md:hidden h-[64px] bg-bg-card border-b border-border-light flex items-center justify-between px-4 shadow-small z-10">
           <div className="flex items-center">
              <img src="/logo4.png" alt="JHY Techs" className="h-7 w-auto object-contain mr-2" />
           </div>
           <div className="flex items-center space-x-3">
             <NotificationBell />
             <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="text-text-secondary hover:text-text-primary transition-colors focus:outline-none p-1">
               {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
             </button>
           </div>
        </header>

        {/* Mobile Menu Overlay */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="md:hidden fixed inset-0 z-50 bg-[#1A1A1A]/40 backdrop-blur-sm flex"
            >
               <motion.div 
                initial={{ x: -280 }}
                animate={{ x: 0 }}
                exit={{ x: -280 }}
                transition={{ type: "spring", damping: 25, stiffness: 200 }}
                className="w-72 bg-bg-sidebar h-full flex flex-col shadow-floating border-r border-border-light"
               >
                  <div className="h-[64px] flex items-center justify-between px-6 border-b border-border-light">
                    <span className="text-text-primary text-[18px] font-bold tracking-tight">Menu</span>
                    <button onClick={() => setIsMobileMenuOpen(false)} className="text-text-muted hover:text-text-primary focus:outline-none bg-bg-soft p-1.5 rounded-[6px] transition-colors">
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  <nav className="flex-1 px-4 py-6 space-y-1.5">
                    {navItems.map((item) => {
                      const isActive = location.pathname === item.href || (item.href !== "/" && location.pathname.startsWith(item.href));
                      return (
                        <Link
                          key={item.name}
                          to={item.href}
                          onClick={() => setIsMobileMenuOpen(false)}
                          className={`flex items-center px-4 py-3 rounded-[8px] text-[14px] font-medium transition-colors ${
                            isActive ? "bg-primary-light text-primary" : "text-text-secondary hover:bg-bg-soft hover:text-text-primary"
                          }`}
                        >
                          <item.icon className={`w-5 h-5 mr-3 ${isActive ? 'text-primary' : 'text-text-secondary'}`} />
                          {item.name}
                        </Link>
                      );
                    })}
                    
                    <div className="mt-8 pt-6 border-t border-border-light">
                      <div className="flex items-center px-4 mb-4">
                         <div className="w-10 h-10 rounded-[8px] bg-primary-light flex items-center justify-center mr-3 text-[14px] font-bold text-primary">
                            {user?.fullName?.charAt(0) || "U"}
                         </div>
                         <div className="flex-1 truncate">
                           <p className="text-[14px] font-bold text-text-primary truncate">{user?.fullName || "User"}</p>
                           <p className="text-[12px] font-medium text-text-muted truncate">{user?.role || "Client"}</p>
                         </div>
                      </div>
                      <motion.button
                        whileTap={{ scale: 0.97 }}
                        onClick={handleLogout}
                        className="w-full flex items-center px-4 py-3 rounded-[8px] text-[14px] font-medium text-text-secondary hover:bg-[#FFF5F5] hover:text-[#E53E3E] transition-colors"
                      >
                        <LogOut className="w-5 h-5 mr-3" />
                        Sign Out
                      </motion.button>

                      <div className="mt-6 pt-6 border-t border-border-light/30 text-center">
                        <p className="text-[10px] font-bold text-text uppercase tracking-[0.2em] opacity-50">
                          v0.1.0 • JHY Techs
                        </p>
                      </div>
                    </div>
                  </nav>
               </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Top Header (Desktop) */}
        <header className="hidden md:flex h-[64px] bg-bg-card border-b border-border-light items-center justify-between px-8 z-10 sticky top-0">
           <h1 className="text-[18px] font-bold text-text-primary tracking-tight">
             {location.pathname === "/" || location.pathname === "/leads" ? "My Leads" : location.pathname.substring(1).replace("-", " ").replace(/\b\w/g, l => l.toUpperCase())}
           </h1>
           <div className="flex items-center space-x-4">
              <NotificationBell />
              <button 
                onClick={toggleTheme}
                className="p-2 text-text-secondary hover:text-primary transition-colors bg-bg-soft rounded-full border border-border-light shadow-small"
                aria-label="Toggle Dark Mode"
              >
                {isDarkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </button>
              <span className="text-[11px] font-bold tracking-widest text-text-muted bg-bg-soft border border-border-light px-3 py-1 rounded-pill uppercase">
                Client Portal
              </span>
           </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 overflow-x-hidden overflow-y-auto bg-bg-app p-4 md:p-8 relative">
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
              className="h-full w-full"
            >
              <Outlet />
            </motion.div>
          </AnimatePresence>
        </main>
      </div>
    </div>
  );
}
